import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";

import { defaultBilling } from "../consts/billing";
import { GET_BILLINGS_LIST_URL } from "../middleware/routes";
import { RootState } from "../store";
import { IBilling, IBillingsState } from "../types/billings";
import { IPagination } from "../types/pagination";
import { Status } from "../types/state";
import { axiosRequestApi } from "../utils/axiosRequest";
import { getSkeletonMockedArray } from "../utils/tables/getSkeletonMockedArray";
import { getSkeletonSuccessArray } from "../utils/tables/getSkeletonSuccessArray";
import { adaptBillingsResponseForFrontend } from "./utils/billingsAdapt";

export const defaultPagination = {
  page: 1,
  limit: 100,
};

const defaultBillings: IBilling[] = getSkeletonMockedArray(defaultBilling, 100, 0);

const initBillingsState: IBillingsState = {
  requestId: "",
  status: Status.idle,
  errorMsg: null,
  list: structuredClone(defaultBillings),
  isNextPageEnable: false,
  scrollTop: 0,
  pagination: defaultPagination,
};

export const billingsSlice = createSlice({
  name: "billings",
  initialState: initBillingsState,
  reducers: {
    cleanUpBillingsList: (state: IBillingsState) => {
      state.status = Status.success;
      state.errorMsg = null;
      state.isNextPageEnable = false;
      state.list = structuredClone(defaultBillings);
    },
    replaceBillings: (state: IBillingsState, action: PayloadAction<IBilling>) => {
      const items = [...state.list];
      const newItems = items.map((item) => {
        if (item.billingId === action.payload.billingId) {
          return action.payload;
        }
        return item;
      });

      state.list = newItems;
    },
    setBillingsListScrollTop: (state: IBillingsState, action: PayloadAction<number>) => {
      state.scrollTop = action.payload;
    },
    setBillingsListPagination: (state: IBillingsState, action: PayloadAction<IPagination>) => {
      state.pagination = action.payload;
    },
    resetBillingsState: () => {
      return initBillingsState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getBillingsListThunk.pending, (state, action) => {
        state.status = Status.requesting;
        state.requestId = action.meta.requestId;
      })
      .addCase(getBillingsListThunk.fulfilled, (state, action) => {
        if (state.requestId !== action.meta.requestId) {
          return;
        }

        const itemsFromState: IBilling[] = state.list;
        const itemsFromResponse: IBilling[] = action.payload;
        const newItems = getSkeletonSuccessArray(itemsFromState, itemsFromResponse);

        state.list = newItems;

        state.isNextPageEnable = false;
        state.errorMsg = null;
        state.status = Status.success;
      })
      .addCase(getBillingsListThunk.rejected, (state) => {
        state.status = Status.error;
        state.errorMsg = "there has been an error";
      });
  },
});

export const getBillingsListThunk = createAsyncThunk<any, void, { state: RootState }>(
  "billings/getBillingsListThunk",
  async (_) => {
    const response = await axiosRequestApi.get(GET_BILLINGS_LIST_URL);

    return adaptBillingsResponseForFrontend(response.data);
  }
);

export const {
  cleanUpBillingsList,
  setBillingsListPagination,
  setBillingsListScrollTop,
  replaceBillings,
  resetBillingsState,
} = billingsSlice.actions;

export const billingsReducer = billingsSlice.reducer;
